import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Copy, Eye, ListMusic, Pencil, Play, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, errMsg } from "@/lib/api";
import { MODES } from "@/modes";
import { EMPTY_FILTER, ListFilter, Pager, useFiltered, usePaged } from "@/components/teacher/ListFilter";
import { AccessBadge, ShareButton, ShareDialog } from "@/components/teacher/ShareDialog";
import { EMPTY_TAGS, tagsOf, LikeButton, TagChips, TagPicker } from "@/components/teacher/Social";

export const playlistKey = "fchem_playlist_run";
const plText = (p) => `${p.title} ${p.description} ${p.items.map((i) => i.title).join(" ")}`;

const AddItem = ({ onAdd }) => {
  const [kind, setKind] = useState("activity");
  const [acts, setActs] = useState([]);
  const [pools, setPools] = useState([]);
  const [modes, setModes] = useState([]);
  const [actId, setActId] = useState("");
  const [mode, setMode] = useState("quiz");
  const [poolIds, setPoolIds] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/activities"), api.get("/pools"), api.get("/modes")]).then(([a, p, m]) => {
      setActs(a.data); setPools(p.data); setModes(m.data.filter((x) => MODES[x.key] && x.key !== "activity"));
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  const add = () => {
    if (kind === "activity") {
      const a = acts.find((x) => x.id === actId);
      if (a) onAdd({ type: "activity", activity_id: a.id, title: a.title, settings: { team_count: 0 } });
    } else if (poolIds.length) {
      const m = modes.find((x) => x.key === mode);
      onAdd({ type: "game", mode, pool_ids: poolIds, settings: m?.default_settings || {}, title: pools.filter((p) => poolIds.includes(p.id)).map((p) => p.name).join(", ") });
      setPoolIds([]);
    }
  };
  return (
    <div className="rounded-xl border border-dashed border-white/20 p-3 space-y-3" data-testid="playlist-add-item">
      <div className="flex gap-2">
        <Button size="sm" variant={kind === "activity" ? "secondary" : "ghost"} onClick={() => setKind("activity")} data-testid="playlist-kind-activity">Etkinlik</Button>
        <Button size="sm" variant={kind === "game" ? "secondary" : "ghost"} onClick={() => setKind("game")} data-testid="playlist-kind-game">Havuzdan oyun</Button>
      </div>
      {kind === "activity" ? (
        <Select value={actId} onValueChange={setActId}>
          <SelectTrigger data-testid="playlist-activity-select"><SelectValue placeholder="Etkinlik seç" /></SelectTrigger>
          <SelectContent>{acts.map((a) => <SelectItem key={a.id} value={a.id}>{a.title} · {a.owner_name}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="playlist-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>{modes.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {pools.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-white/5 cursor-pointer">
                <Checkbox checked={poolIds.includes(p.id)} onCheckedChange={() => setPoolIds((ids) => (ids.includes(p.id) ? ids.filter((x) => x !== p.id) : [...ids, p.id]))} data-testid={`playlist-pool-${p.id}`} />
                {p.name} <span className="text-slate-500">({p.question_count})</span>
              </label>
            ))}
          </div>
        </>
      )}
      <Button size="sm" onClick={add} data-testid="playlist-add-item-button"><Plus className="h-4 w-4 mr-1" />Listeye ekle</Button>
    </div>
  );
};

const Editor = ({ playlist, onOpenChange, onSaved }) => {
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState(EMPTY_TAGS);
  useEffect(() => {
    if (!playlist) return;
    setTitle(playlist.title || "");
    setItems(playlist.items || []);
    setTags(tagsOf(playlist));
  }, [playlist]);
  const move = (i, d) => setItems((xs) => { const a = [...xs]; [a[i], a[i + d]] = [a[i + d], a[i]]; return a; });
  const save = async () => {
    const body = { title, items: items.map(({ type, activity_id, mode, pool_ids, settings, label }) => ({ type, activity_id, mode, pool_ids, settings, label })) };
    try {
      const { data } = playlist.id ? await api.put(`/playlists/${playlist.id}`, body) : await api.post("/playlists", body);
      if (tags.theme_id || tags.grade || playlist.theme_id || playlist.grade) await api.put(`/tags/playlists/${data.id}`, tags);
      toast.success("Oyun listesi kaydedildi");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={Boolean(playlist)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="playlist-editor">
        <DialogHeader><DialogTitle className="font-display text-3xl">{playlist?.id ? "Listeyi düzenle" : "Yeni oyun listesi"}</DialogTitle></DialogHeader>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Liste adı (örn. 9. sınıf – Atom haftası)" className="h-12 text-lg" data-testid="playlist-title-input" />
        <ol className="space-y-2" data-testid="playlist-items">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2" data-testid={`playlist-item-${i}`}>
              <span className="font-display text-xl font-bold text-[#FACC15] w-6">{i + 1}.</span>
              <span className="flex-1 min-w-0"><b className="block truncate">{it.title}</b><span className="text-xs text-slate-400">{it.type === "activity" ? "Etkinlik" : MODES[it.mode]?.label || it.mode}</span></span>
              <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`playlist-item-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" disabled={i === items.length - 1} onClick={() => move(i, 1)} data-testid={`playlist-item-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-400" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`playlist-item-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ol>
        <AddItem onAdd={(it) => setItems((xs) => [...xs, it])} />
        <TagPicker value={tags} onChange={setTags} />
        <DialogFooter><Button onClick={save} disabled={title.trim().length < 2} className="font-bold" data-testid="playlist-save">Kaydet</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const startPlaylistItem = async (navigate, pl, index) => {
  try {
    const { data } = await api.post(`/playlists/${pl.id}/start/${index}`);
    localStorage.setItem(playlistKey, JSON.stringify({ id: pl.id, title: pl.title, index, total: pl.items.length, code: data.code }));
    navigate(`/ogretmen/oyun/${data.code}`);
  } catch (e) { toast.error(errMsg(e)); }
};

export const previewPlaylist = async (pl, index) => {
  try {
    const { data } = await api.post("/preview", { playlist_id: pl.id, index });
    return `/onizleme/${data.code}?pl=${pl.id}&i=${index}&n=${pl.items.length}`;
  } catch (e) { toast.error(errMsg(e)); return null; }
};

const PlaylistCard = ({ p, onEdit, onShare, onDelete, onDuplicate }) => {
  const navigate = useNavigate();
  return (
    <div className="panel p-5 space-y-3" data-testid={`playlist-card-${p.id}`}>
      <p className="eyebrow text-[#FACC15]">{p.items.length} oyun</p>
      <p className="font-display text-2xl font-bold">{p.title}</p>
      <p className="text-xs text-slate-500">Oluşturan: <span className="text-slate-300">{p.owner_name}</span> <AccessBadge item={p} /></p>
      <div className="flex items-center gap-2"><LikeButton kind="playlists" item={p} /><TagChips item={p} /></div>
      <ol className="text-sm text-slate-300 space-y-0.5">
        {p.items.map((it, i) => <li key={i} className={`truncate ${it.missing ? "line-through text-slate-500" : ""}`}>{i + 1}. {it.title}</li>)}
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!p.items.length} onClick={() => startPlaylistItem(navigate, p, 0)} data-testid={`playlist-start-${p.id}`}><Play className="h-4 w-4 mr-1" />Dersi başlat</Button>
        <Button size="sm" variant="outline" disabled={!p.items.length} onClick={async () => { const url = await previewPlaylist(p, 0); if (url) window.open(url, "_blank"); }} data-testid={`playlist-preview-${p.id}`}><Eye className="h-4 w-4 mr-1" />Önizle</Button>
        {p.mine && <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`playlist-edit-${p.id}`}><Pencil className="h-4 w-4 mr-1" />Düzenle</Button>}
        <Button size="sm" variant="outline" onClick={() => onDuplicate(p)} data-testid={`playlist-duplicate-${p.id}`}><Copy className="h-4 w-4 mr-1" />Çoğalt</Button>
        <ShareButton item={p} onClick={onShare} />
        {p.mine && <Button size="sm" variant="ghost" className="text-red-400" onClick={() => onDelete(p)} data-testid={`playlist-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
};

export default function PlaylistsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const load = () => api.get("/playlists").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const shown = useFiltered(items, filter, plText);
  const pg = usePaged(shown, 6);
  const duplicate = async (p) => { try { await api.post(`/playlists/${p.id}/duplicate`); toast.success("Liste çoğaltıldı"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async () => { try { await api.delete(`/playlists/${removing.id}`); toast.success("Liste silindi"); load(); } catch (e) { toast.error(errMsg(e)); } setRemoving(null); };
  return (
    <div className="space-y-8" data-testid="playlists-page">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1">
          <p className="eyebrow">Ders akışı</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-1 flex items-center gap-3"><ListMusic className="h-10 w-10 text-[#FACC15]" />Oyun Listeleri</h1>
          <p className="text-slate-400 mt-1">Bir ders için etkinlik ve oyunları sıraya koy; derste "Sıradakini başlat" ile sırayla aç.</p>
        </div>
        <Button onClick={() => setEditing({})} className="font-bold" data-testid="playlist-new-button"><Plus className="h-4 w-4 mr-2" />Yeni liste</Button>
      </div>
      {items.length > 0 && <ListFilter items={items} value={filter} onChange={setFilter} placeholder="Liste ya da öğretmen ara…" />}
      {items.length === 0 && <div className="panel p-10 text-slate-400" data-testid="playlists-empty">Henüz oyun listesi yok.</div>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {pg.items.map((p) => <PlaylistCard key={p.id} p={p} onEdit={setEditing} onShare={setSharing} onDelete={setRemoving} onDuplicate={duplicate} />)}
      </div>
      <Pager pg={pg} testId="playlists-pager" />
      <Editor playlist={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} />
      <ShareDialog kind="playlists" item={sharing} onOpenChange={(o) => !o && setSharing(null)} onSaved={load} />
      <ConfirmDialog open={Boolean(removing)} onOpenChange={(o) => !o && setRemoving(null)} title="Listeyi sil" description={`"${removing?.title || ""}" listesi silinsin mi? İçindeki etkinlik ve havuzlar silinmez.`} confirmText="Sil" onConfirm={remove} />
    </div>
  );
}
